import React from 'react';
import {
  useLoaderData,
  useLocation,
  useNavigate,
  useParams,
  useRevalidator
} from 'react-router-dom';
import clsx from 'clsx';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { MountableSlideout } from '@oysterjs/ui/slideout';
import { Button } from '@oysterjs/ui/button';
import { Spinner } from '@oysterjs/ui/spinner';
import { Await } from '@oysterjs/ui/async';
import {
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogActions
} from '@oysterjs/ui/dialog';
import {
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
  Description
} from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { ApolloClient, ApolloError, NormalizedCacheObject } from '@apollo/client';
import {
  AgencyUserFieldsFragment,
  ChildAgencyFieldsFragment,
  Resource,
  Action,
  Scope,
  AgencyUserState,
  UpdateSubscriptionDocument,
  UpdateSubscriptionMutation,
  UpdateSubscriptionMutationVariables
} from '../../../api/graphql';
import { client } from '../../../api/client';
import { SeatProgressBar } from '@oysterjs/ais/components/settings/usage/seat-progress-bar';
import { UserList } from '@oysterjs/ais/components/settings/subscription/user-list';
import {
  AddUserDialog,
  DisableUserDialog,
  EnableUserDialog,
  EditUserDialog
} from '@oysterjs/ais/components/settings/subscription/user-dialogs';
import { usePermissions } from '../../../components/permissions';

export const EditAgencyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ agencyId: string }>();

  const loader = useLoaderData() as {
    data: Promise<{
      childAgencies?: ChildAgencyFieldsFragment[];
    }>;
  };

  const onClose = () => {
    if (location.key === 'default') {
      navigate('/app/settings/subscription');
    } else {
      navigate(-1);
    }
  };

  return (
    <MountableSlideout onClose={onClose} title="Manage Subagency">
      <React.Suspense
        fallback={
          <div className="flex items-center justify-center py-12">
            <Spinner />
          </div>
        }
      >
        <Await resolve={loader?.data}>
          {(data) => {
            const agency = data?.childAgencies?.find((a) => a.id === params.agencyId);

            if (!agency) {
              return (
                <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
                  <p>Agency not found.</p>
                </div>
              );
            }

            return <EditAgencyContent agency={agency} />;
          }}
        </Await>
      </React.Suspense>
    </MountableSlideout>
  );
};

const EditAgencyContent = ({ agency }: { agency: ChildAgencyFieldsFragment }) => {
  const { hasPermission } = usePermissions();
  const { revalidate } = useRevalidator();

  const [showAddUserDialog, setShowAddUserDialog] = React.useState(false);
  const [showManageAllocationDialog, setShowManageAllocationDialog] = React.useState(false);
  const [disableUserId, setDisableUserId] = React.useState<string | null>(null);
  const [enableUserId, setEnableUserId] = React.useState<string | null>(null);
  const [editUser, setEditUser] = React.useState<AgencyUserFieldsFragment | null>(null);

  const activeUsers = agency.users?.filter((u) => u.state === AgencyUserState.Active).length ?? 0;
  const voiceAgentsInUse = agency.voiceAgents?.length ?? 0;

  const refreshData = () => {
    revalidate();
  };

  return (
    <>
      <div>
        {/* Agency Name */}
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
          {agency.name}
        </h3>

        {/* Allocation Section */}
        <div className="mb-6">
          <h4 className="text-base font-body font-medium text-neutral-900 dark:text-white mb-2">
            Resource Allocation
          </h4>

          {/* Seat Allocation */}
          <div className="mb-4">
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">Seats</p>
            <SeatProgressBar
              totalSeats={agency.subscription?.numSeats ?? 0}
              activeSeats={activeUsers}
            />
          </div>

          {/* Voice Agent Allocation */}
          {!!agency.subscription?.numVoiceAgents && (
            <div className="mb-4">
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">Voice Agents</p>
              <div className="text-sm text-neutral-900 dark:text-neutral-100">
                {voiceAgentsInUse} / {agency.subscription?.numVoiceAgents ?? 0} in use
              </div>
            </div>
          )}

          <div className="mt-3">
            <Button color="light" onClick={() => setShowManageAllocationDialog(true)}>
              Manage Allocation
            </Button>
          </div>
        </div>

        {/* User Management Section */}
        <div>
          <h4 className="text-base font-body font-medium text-neutral-900 dark:text-white mb-2">
            Users
          </h4>

          <div className="border-t border-b py-4 border-neutral-200 dark:border-neutral-700">
            <UserList
              users={agency.users || []}
              onDisable={(userId) => setDisableUserId(userId)}
              onEnable={(userId) => setEnableUserId(userId)}
              onEdit={(user) => setEditUser(user)}
              compact
              emptyMessage="No users yet. Add a user to get started."
              scope={Scope.SubOrganization}
            />
          </div>

          {hasPermission(Resource.AgencyUser, Action.Create) && (
            <div className="mt-4">
              <Button color="sky" onClick={() => setShowAddUserDialog(true)}>
                Add User
              </Button>
            </div>
          )}
        </div>
      </div>

      <ManageAllocationDialog
        client={client}
        open={showManageAllocationDialog}
        onClose={() => setShowManageAllocationDialog(false)}
        onSuccess={refreshData}
        agencyId={agency.id}
        currentSeats={agency.subscription?.numSeats ?? 0}
        minSeats={activeUsers}
        currentVoiceAgents={agency.subscription?.numVoiceAgents ?? 0}
        minVoiceAgents={voiceAgentsInUse}
      />
      <AddUserDialog
        open={showAddUserDialog}
        onClose={() => setShowAddUserDialog(false)}
        onSuccess={refreshData}
        agencyId={agency.id}
      />
      <DisableUserDialog
        client={client}
        open={!!disableUserId}
        userId={disableUserId}
        onClose={() => setDisableUserId(null)}
        onSuccess={refreshData}
      />
      <EnableUserDialog
        client={client}
        open={!!enableUserId}
        userId={enableUserId}
        onClose={() => setEnableUserId(null)}
        onSuccess={refreshData}
      />
      <EditUserDialog
        client={client}
        open={!!editUser}
        user={editUser}
        onClose={() => setEditUser(null)}
        onSuccess={refreshData}
        agencyId={agency.id}
      />
    </>
  );
};

export type ManageAllocationDialogProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  agencyId: string;
  currentSeats: number;
  minSeats: number;
  currentVoiceAgents: number;
  minVoiceAgents: number;
  client: ApolloClient<NormalizedCacheObject>;
};

export const ManageAllocationDialog: React.FC<ManageAllocationDialogProps> = ({
  open,
  onClose,
  onSuccess,
  agencyId,
  currentSeats,
  minSeats,
  currentVoiceAgents,
  minVoiceAgents,
  client
}) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [seats, setSeats] = React.useState(currentSeats);
  const [voiceAgents, setVoiceAgents] = React.useState(currentVoiceAgents);

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open) {
      setSeats(currentSeats);
      setVoiceAgents(currentVoiceAgents);
      setError(null);
      setLoading(false);
    }
  }, [open, currentSeats, currentVoiceAgents]);

  const effectiveMinSeats = Math.max(1, minSeats);
  const effectiveMinVoiceAgents = Math.max(0, minVoiceAgents);

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await client.mutate<
        UpdateSubscriptionMutation,
        UpdateSubscriptionMutationVariables
      >({
        mutation: UpdateSubscriptionDocument,
        variables: {
          input: {
            agencyId,
            seats,
            voiceAgents
          }
        }
      });

      if (result.errors?.length) {
        throw new ApolloError({ graphQLErrors: result.errors });
      }

      if (result.data?.updateSubscription) {
        onSuccess();
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while updating allocation');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const hasChanges = seats !== currentSeats || voiceAgents !== currentVoiceAgents;

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Manage Allocation</DialogTitle>
      <DialogDescription>
        Adjust the resource allocation for this subagency. Resources cannot be reduced below the
        number currently in use.
      </DialogDescription>
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Number of Seats</Label>
              <Description>
                Currently {currentSeats} {currentSeats === 1 ? 'seat' : 'seats'} allocated. Minimum{' '}
                {effectiveMinSeats} required based on the number of active users.
              </Description>
              <div data-slot="control" className="flex items-center gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setSeats((s) => Math.max(effectiveMinSeats, s - 1))}
                  disabled={seats <= effectiveMinSeats}
                  className={clsx(
                    'flex h-8 w-8 items-center justify-center rounded-md border',
                    seats <= effectiveMinSeats
                      ? 'border-neutral-200 bg-neutral-50 text-neutral-300 cursor-not-allowed dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-600'
                      : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700'
                  )}
                >
                  <MinusIcon className="h-4 w-4" />
                </button>
                <div className="w-20">
                  <Input
                    type="number"
                    min={effectiveMinSeats}
                    value={seats}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      if (!isNaN(value) && value >= effectiveMinSeats) {
                        setSeats(value);
                      }
                    }}
                    className="text-center"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setSeats((s) => s + 1)}
                  className="flex h-8 w-8 items-center justify-center rounded-md border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
              </div>
            </Field>
            <Field>
              <Label>Number of Voice Agents</Label>
              <Description>
                Currently {currentVoiceAgents} voice {currentVoiceAgents === 1 ? 'agent' : 'agents'}{' '}
                allocated. Minimum {effectiveMinVoiceAgents} required based on the number in use.
              </Description>
              <div data-slot="control" className="flex items-center gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setVoiceAgents((v) => Math.max(effectiveMinVoiceAgents, v - 1))}
                  disabled={voiceAgents <= effectiveMinVoiceAgents}
                  className={clsx(
                    'flex h-8 w-8 items-center justify-center rounded-md border',
                    voiceAgents <= effectiveMinVoiceAgents
                      ? 'border-neutral-200 bg-neutral-50 text-neutral-300 cursor-not-allowed dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-600'
                      : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700'
                  )}
                >
                  <MinusIcon className="h-4 w-4" />
                </button>
                <div className="w-20">
                  <Input
                    type="number"
                    min={effectiveMinVoiceAgents}
                    value={voiceAgents}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      if (!isNaN(value) && value >= effectiveMinVoiceAgents) {
                        setVoiceAgents(value);
                      }
                    }}
                    className="text-center"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setVoiceAgents((v) => v + 1)}
                  className="flex h-8 w-8 items-center justify-center rounded-md border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
              </div>
            </Field>
          </FieldGroup>
        </Fieldset>
        {error && <ErrorMessage className="mt-4">{error}</ErrorMessage>}
      </DialogBody>
      <DialogActions>
        <Button color="light" onClick={handleClose}>
          Cancel
        </Button>
        <Button color="sky" onClick={handleSubmit} loading={loading} disabled={!hasChanges}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};
